import includes from "lodash/includes";
import { useIsSmartPlannerView } from "PFApp/booking/hooks/use_is_smart_planner_view";
import {
  useBookingModuleStore,
  useBookingModuleStoreActions,
  useCalendarRoot,
  useWorkforceProfilesDataIfAvailable
} from "PFApp/booking/store";
import { getActivityItemsForActivityWarnings } from "PFApp/booking/store/provider/additional_data/use_rows_additional_data.utils";
import { CalendarMetric, CalendarView } from "PFApp/booking/types";
import { BookingsVisibility } from "PFApp/booking/types/bookings_visibility";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { RowType } from "PFApp/booking/types/row";
import { isProjects, isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { JsonValue, stringify } from "PFCore/helpers/use_deterministic_stringify";
import { useActivityWarnings } from "PFCore/hooks/queries/activity";
import { WorkforceItem } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { useProfilesWarnings } from "PFCore/hooks/queries/profile";
import {
  CalendarGranularity,
  useProfilesAvailabilities
} from "PFCore/hooks/queries/profile/use_profiles_availabilities";
import { useEffect, useMemo } from "react";

import { useRowBookings } from "./use_row_bookings";

const loadingKey = "additionalDataLoading";

/**
 * The data are duplicated to the store to improve performance: less useQuery instances in children components
 */
export const useRowsAdditionalData = () => {
  const calendarRange = useBookingModuleStore((state) => state.calendarRange);
  const calendarRoot = useCalendarRoot();
  const rows = useBookingModuleStore((state) => state[calendarRoot].rows);
  const visibleRowsRange = useBookingModuleStore((state) => state.visibleRowsRange);
  const rowsByKey = useBookingModuleStore((state) => state[calendarRoot].rowsByKey);
  const { view, metric, calendarGranularity } = useWorkforceProfilesDataIfAvailable(
    "view",
    "metric",
    "calendarGranularity"
  );
  const allBookingsModeRowsState = useBookingModuleStore((state) =>
    isProjects(calendarRoot) ? state[calendarRoot].allBookingsModeRowsState : null
  );
  const isRoleLevelHiddenForEngagementsRoot = useBookingModuleStore(
    (state) =>
      calendarRoot === CalendarRoot.ProjectsEngagements &&
      state[CalendarRoot.ProjectsEngagements].isRoleLevelHidden
  );
  const bookingsVisibilityVisibleRowKeys = useBookingModuleStore((state) =>
    isWorkforceProfiles(calendarRoot) &&
    state[CalendarRoot.WorkforceProfiles].bookingsVisibility === BookingsVisibility.CurrentZoom
      ? state[CalendarRoot.WorkforceProfiles].bookingsVisibilityVisibleRowKeys
      : null
  );
  const {
    [calendarRoot]: { setRowsLoadingByKey, setBookingsByRowKey, setWarningsByProfile }
  } = useBookingModuleStoreActions();
  const setAvailabilitiesByProfile = useBookingModuleStore((state) =>
    isWorkforceProfiles(calendarRoot)
      ? state.actions[CalendarRoot.WorkforceProfiles].setAvailabilitiesByProfile
      : null
  );
  const setWarningsByActivity = useBookingModuleStore((state) =>
    isProjects(calendarRoot) ? state.actions[calendarRoot].setWarningsByActivity : null
  );

  const visibleRows = useMemo(() => {
    const rowsLimitedByBookingsVisibility = bookingsVisibilityVisibleRowKeys
      ? rows.filter((row) => bookingsVisibilityVisibleRowKeys.has(row.key))
      : rows;
    // apply virtualization filtering on top of the rows filtered by CurrentZoom
    return visibleRowsRange !== null
      ? rowsLimitedByBookingsVisibility.slice(visibleRowsRange.minIndex, visibleRowsRange.maxIndex + 1)
      : [];
  }, [rows, visibleRowsRange, bookingsVisibilityVisibleRowKeys]);

  const isSmartPlannerView = useIsSmartPlannerView();

  // in case CurrentZoom is ON, we need bookings for all rows to determine which rows to display
  const rowsWithBookingsToBeFetched = bookingsVisibilityVisibleRowKeys ? rows : visibleRows;

  const { bookingsByRowKey, bookingsLoadingByRowKey } = useRowBookings({
    calendarRoot,
    view,
    isSmartPlannerView,
    allBookingsModeRowsState,
    rowsByKey,
    rows: rowsWithBookingsToBeFetched
  });

  const activityItems = getActivityItemsForActivityWarnings({
    rows: visibleRows,
    calendarRoot,
    isRoleLevelHiddenForEngagementsRoot
  });
  const { warningsByActivity, activityWarningsLoadingByRowKey } = useActivityWarnings(activityItems);

  const { warningsByProfile, profileWarningsLoadingByRowKey } = useProfilesWarnings({
    profiles: visibleRows
      .filter(({ type }) => type === RowType.Profile)
      .map(({ key, original }) => ({ rowKey: key, id: (original as WorkforceItem).id })),
    calendarRange
  });

  const shouldFetchHeatMapData =
    isWorkforceProfiles(calendarRoot) &&
    includes([CalendarMetric.Utilization, CalendarMetric.Availability, CalendarMetric.TotalHours], metric) &&
    !includes([CalendarView.Compressed, CalendarView.BookingsOnly], view);

  const profiles = shouldFetchHeatMapData
    ? visibleRows
        .filter(({ type }) => type === RowType.Profile)
        .map(({ key, original }) => ({ rowKey: key, id: (original as WorkforceItem).id }))
    : [];

  const { availabilitiesByProfile, availabilitiesLoadingByRowKey } = useProfilesAvailabilities({
    profiles,
    dateRange: calendarRange,
    calendarGranularity: calendarGranularity ?? CalendarGranularity.Daily,
    enabled: shouldFetchHeatMapData
  });

  const rowsLoadingByKey = useMemo(
    () =>
      rows.reduce(
        (result, { key }) => ({
          ...result,
          [key]: {
            [loadingKey]:
              bookingsLoadingByRowKey[key] ||
              profileWarningsLoadingByRowKey[key] ||
              (isProjects(calendarRoot) && activityWarningsLoadingByRowKey[key]) ||
              (isWorkforceProfiles(calendarRoot) && availabilitiesLoadingByRowKey[key])
          }
        }),
        {} as Record<string, Record<string, boolean>>
      ),
    [
      rows,
      calendarRoot,
      bookingsLoadingByRowKey,
      activityWarningsLoadingByRowKey,
      availabilitiesLoadingByRowKey,
      profileWarningsLoadingByRowKey
    ]
  );

  useEffect(() => {
    setBookingsByRowKey(bookingsByRowKey);
  }, [stringify(bookingsByRowKey as JsonValue)]);

  useEffect(() => {
    setWarningsByProfile(warningsByProfile);
  }, [stringify(warningsByProfile)]);

  useEffect(() => {
    setRowsLoadingByKey(rowsLoadingByKey, loadingKey);
  }, [stringify(rowsLoadingByKey)]);

  useEffect(() => {
    if (isWorkforceProfiles(calendarRoot)) {
      setAvailabilitiesByProfile?.(availabilitiesByProfile);
    }
  }, [stringify(availabilitiesByProfile)]);

  useEffect(() => {
    if (isProjects(calendarRoot)) {
      setWarningsByActivity?.(warningsByActivity);
    }
  }, [stringify(warningsByActivity)]);
};
